/* eslint-disable import/no-cycle */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Button, Grid } from '@mantine/core';
import { Link } from 'react-router-dom';
import AppStack from '../../../../common/AppStack';
import WsSectionContainer from '../WsSectionContainer';
import { Context as StoreUserContext } from '../../../../../providers/StoreUserContextProvider';
import { IMAGE_DISPLAY_ENUM, SORT_BY_ENUM } from './wsDesignGridConfig';
import WsDesignCard from './WsDesignCard';
import AppText from '../../../../common/AppText';
import {
  doesResourcesHaveProducts,
  getSelectedResourceBaseNavigationUrl,
  getWebstoreHeadingSize
} from '../../../../../helpers/webstoreHelper';

const WsDesignGrid = ({
  blocks,
  config,
  themeConfig,
  colorSchemes,
  mediaQueries,
  pathname,
  ecomStoreTheme,
  onNavigate,
  baseStoreUrl,
  editModeEnabled,
  search,
  resourceNavigation,
  selectedResources,
  ...rest
}) => {
  const { state } = useContext(StoreUserContext);
  const { isTabletOrSmaller } = mediaQueries;
  const colorScheme = colorSchemes[config.colorScheme - 1] ?? colorSchemes[0];
  const squareRatio = config.imageRatio === IMAGE_DISPLAY_ENUM.SQUARE;
  const hasFetched =
    state.ecomStoreDesigns.filter?.pkEcomStore ===
    ecomStoreTheme.ecomStore.pkEcomStore;

  const loading =
    ecomStoreTheme.ecomStore.ecomStoreDesigns.length !== 0 &&
    (!hasFetched || state.ecomStoreDesigns.loading);

  const gridColumnCount = isTabletOrSmaller
    ? config.mobileColumnCount * 1 ?? 1
    : config.desktopColumnCount * 1 ?? 6;

  const {
    ecomCategory,
    ecomSubcategory,
    ecomStoreDesign,
    ecomStoreCollection
  } = selectedResources;
  const designs = state.ecomStoreDesigns.value.filter((d) =>
    doesResourcesHaveProducts(ecomStoreTheme, {
      pkEcomCategory: ecomCategory?.value,
      pkEcomVendorDesign: d.pkEcomVendorDesign,
      pkEcomStoreCollection: ecomStoreCollection?.pkEcomStoreCollection,
      ecomSubcategory: ecomSubcategory?.value
    })
  );
  const baseResourceUrl = `${baseStoreUrl}${getSelectedResourceBaseNavigationUrl(
    resourceNavigation,
    'designs'
  )}`;

  return (
    <WsSectionContainer
      colorScheme={colorScheme}
      config={config}
      mediaQueries={mediaQueries}
      themeConfig={themeConfig}
      {...rest}
    >
      <AppStack style={{ width: '100%' }}>
        {config.headingText && (
          <AppText
            style={{
              fontWeight: 600,
              fontSize: getWebstoreHeadingSize(config.headingSize)
            }}
          >
            {config.headingText}
          </AppText>
        )}

        {!loading ? (
          designs.length === 0 ? (
            <AppStack style={{ alignItems: 'center', padding: 40 }}>
              <AppText
                style={{
                  color: colorScheme.textColor,
                  fontSize: 18,
                  fontWeight: '500'
                }}
              >
                No designs found
              </AppText>
              <Button
                color={colorScheme.solidButtonColor}
                component={Link}
                onClick={onNavigate}
                size="md"
                style={{ color: colorScheme.solidButtonLabel }}
                to={`${baseStoreUrl}/products`}
                variant="filled"
              >
                Shop all products
              </Button>
            </AppStack>
          ) : (
            <Grid columns={gridColumnCount}>
              {designs
                .sort((a, b) => {
                  switch (config.sortBy) {
                    case SORT_BY_ENUM.NAME:
                      return a.name.localeCompare(b.name);
                    case SORT_BY_ENUM.NAME_DESC:
                      return b.name.localeCompare(a.name);
                    case SORT_BY_ENUM.DATE:
                      return new Date(b.createdAt) - new Date(a.createdAt);
                    case SORT_BY_ENUM.DATE_DESC:
                      return new Date(a.createdAt) - new Date(b.createdAt);
                    case SORT_BY_ENUM.COUNT:
                      return (
                        b.ecomStoreDesignProductCount -
                        a.ecomStoreDesignProductCount
                      );
                    case SORT_BY_ENUM.COUNT_DESC:
                      return (
                        a.ecomStoreDesignProductCount -
                        b.ecomStoreDesignProductCount
                      );
                    default:
                      return 0;
                  }
                })
                .map((d) => (
                  <Grid.Col key={d.pkEcomVendorDesign} span={1}>
                    <WsDesignCard
                      baseStoreUrl={baseResourceUrl}
                      colorScheme={colorScheme}
                      ecomStoreDesign={d}
                      onNavigate={onNavigate}
                      portraitRatio={!squareRatio}
                    />
                  </Grid.Col>
                ))}
            </Grid>
          )
        ) : (
          <Grid columns={gridColumnCount}>
            {Array.from(Array(gridColumnCount)).map((x, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Grid.Col key={i} span={1}>
                <WsDesignCard portraitRatio={!squareRatio} />
              </Grid.Col>
            ))}
          </Grid>
        )}
      </AppStack>
    </WsSectionContainer>
  );
};

WsDesignGrid.propTypes = {
  baseStoreUrl: PropTypes.string,
  blocks: PropTypes.array,
  colorSchemes: PropTypes.array,
  config: PropTypes.object,
  ecomStoreTheme: PropTypes.object,
  editModeEnabled: PropTypes.bool,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  pathname: PropTypes.string,
  resourceNavigation: PropTypes.array,
  search: PropTypes.string,
  selectedResources: PropTypes.object,
  themeConfig: PropTypes.object
};

export default WsDesignGrid;
